import { useState, useEffect, useCallback } from 'react';
import { Button, Card, Container, Center, Stack, Spinner, Input } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useController } from 'react-hook-form';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import TextEditor from '@/components/ui/text-editor';
import GroupBlock from './elements/group-block';
import api from '@/utils/api';
import useCollectionTypeStore from '@/utils/use-collection-type';
import { ContentType, CollectionType, GroupForm, Content } from '@/lib/types';

interface Type extends CollectionType {
  content_type: ContentType;
}

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  content: z
    .array(
      z.object({
        name: z.string().min(1, 'Group name is required'),
        contents: z.array(z.string()),
      }),
    )
    .optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function CreateCollection() {
  const { collectionTypes } = useCollectionTypeStore();
  const [collectionType, setCollectionType] = useState<CollectionType>();
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [contens, setContents] = useState<Content[]>([]);

  const { workspaceId, collectionTypeId } = useParams<{
    workspaceId: string;
    collectionTypeId: string;
  }>();
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const description = useController({
    control,
    name: 'description',
    defaultValue: '',
  });

  const content = useController({
    control,
    name: 'content',
    defaultValue: [],
  });

  const fetchContentType = useCallback(async () => {
    if (!collectionTypeId) return;

    setIsLoading(true);

    try {
      const collectionType = [...collectionTypes].find(
        (type) => type.id === parseInt(collectionTypeId, 10),
      );

      if (collectionType) {
        setCollectionType(collectionType);
      }

      const collectionTypesRes = await api.get(
        `collection-content-types/collection-types/${collectionTypeId}`,
      );

      if (collectionTypesRes.data) {
        const types = collectionTypesRes.data.map((item: Type) => item.content_type);
        setContentTypes(types);
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load content type',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [collectionTypeId, collectionTypes]);

  useEffect(() => {
    fetchContentType();
  }, [fetchContentType]);

  const onSubmit = async (data: FormData) => {
    if (!collectionTypeId) {
      return;
    }

    try {
      await api.post(`collections`, {
        name: data.name,
        description: data.description,
        collection_type_id: parseInt(collectionTypeId),
        status: 'draft',
        content: {
          groups: data.content || {},
        },
      });

      toaster.create({
        type: 'success',
        title: 'Collection created successfully',
      });

      navigate(`/${workspaceId}/collection-types/${collectionTypeId}/collections/my-collections`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create collection',
      });
    }
  };

  const applyChanges = (groups: GroupForm[], item: Content) => {
    content.field.onChange(groups);
    if (item) {
      setContents((prev) => [...prev, item]);
    }
  };

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header textAlign={'center'}>
        <Card.Title fontSize='3xl'>Create {collectionType?.name || 'Content'}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <Center h='300px'>
            <Spinner size='xl' />
          </Center>
        )}
        <Container maxW={'3xl'}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={4}>
              <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                <Input {...register('name')} />
              </Field>
              <Field label='Description'>
                <TextEditor
                  initialValue={description.field.value}
                  onChange={(newContent: string) => {
                    description.field.onChange(newContent);
                  }}
                />
              </Field>

              {workspaceId && (
                <GroupBlock
                  groups={content.field.value || []}
                  errors={errors.content}
                  contentTypes={contentTypes}
                  workspaceId={parseInt(workspaceId, 10)}
                  contents={contens || []}
                  onChange={applyChanges}
                />
              )}

              <Button
                colorPalette='brand'
                type={'submit'}
                width={'full'}
                mt={5}
                loading={isSubmitting}
                loadingText='Loading...'
              >
                Save
              </Button>
            </Stack>
          </form>
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
